
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { API_ENDPOINTS } from '../../../../utils/config';
import './SupplierSelectionModal.scss';
import QuoteAttachmentsModal from '../../../admin-v2/dashboard/RFQQuotesReviewDashboard/QuoteAttachmentsModal';

interface SupplierSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    requestId: string;
    rfqName?: string;
    onSuccess?: () => void;
}

interface SupplierQuoteRow {
    supplier_id: string;
    supplier_name: string;
    total_price: number;
    total_price_display?: string;
    currency: string;
    lead_time: string;
    payment_terms: string;
    quote_valid_until: string;
}

const SupplierSelectionModal: React.FC<SupplierSelectionModalProps> = ({
    isOpen,
    onClose,
    requestId,
    rfqName,
    onSuccess
}) => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState<SupplierQuoteRow[]>([]);
    const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [requestDetails, setRequestDetails] = useState<any>(null);

    // Attachments State
    const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
    const [attachmentSupplier, setAttachmentSupplier] = useState<{ id: string; name: string } | null>(null);
    const [attachmentCounts, setAttachmentCounts] = useState<Record<string, number>>({});

    // Fetch attachment counts
    useEffect(() => {
        const fetchAttachmentCounts = async () => {
            if (!requestId || !token || quotes.length === 0) return;

            const counts: Record<string, number> = {};
            const uniqueSupplierIds = Array.from(new Set(quotes.map(q => q.supplier_id)));

            await Promise.all(uniqueSupplierIds.map(async (supplierId) => {
                try {
                    const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_MESSAGES(requestId, String(supplierId)), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const result = await response.json();
                    if (result.success && result.data?.messages) {
                        let count = 0;
                        result.data.messages.forEach((msg: any) => {
                            if (msg.direction === 'received' && msg.attachments) {
                                count += msg.attachments.length;
                            }
                        });
                        counts[supplierId] = count;
                    }
                } catch (err) {
                    console.error(`Failed to fetch attachments for ${supplierId}`, err);
                }
            }));

            setAttachmentCounts(prev => ({ ...prev, ...counts }));
        };

        fetchAttachmentCounts();
    }, [requestId, token, quotes]);

    // Fetch quotes summary
    useEffect(() => {
        if (isOpen && requestId && token) {
            fetchQuotes();
            fetchRequestDetails();
        }
    }, [isOpen, requestId, token]);

    const fetchQuotes = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_ENDPOINTS.REQUEST_QUOTES_SUMMARY(requestId), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Failed to fetch quotes');

            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setQuotes(data.data);
            }
        } catch (err: any) {
            console.error('Error fetching quotes:', err);
            setError('Could not load supplier quotes.');
        } finally {
            setLoading(false);
        }
    };

    const fetchRequestDetails = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setRequestDetails(data.data);
                    // Pre-select if already selected
                    if (data.data.selected_supplier_ids && Array.isArray(data.data.selected_supplier_ids)) {
                        setSelectedSupplierIds(data.data.selected_supplier_ids.map(String));
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching request details:', err);
        }
    };

    const formatPrice = (quote: SupplierQuoteRow) => {
        // If display string exists and is not empty, use it
        if (quote.total_price_display && quote.total_price_display !== '—') {
            return quote.total_price_display;
        }
        // Fallback to number formatting
        if (quote.total_price !== undefined && quote.total_price !== null) {
            const price = typeof quote.total_price === 'string' ? parseFloat(quote.total_price) : quote.total_price;
            if (!isNaN(price)) {
                return `${price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${quote.currency || ''}`;
            }
        }
        return '—';
    };

    const handleToggleSupplier = (supplierId: string) => {
        setSelectedSupplierIds(prev => {
            const isSelected = prev.includes(supplierId);
            if (isSelected) {
                return prev.filter(id => id !== supplierId);
            } else {
                return [...prev, supplierId];
            }
        });
    };

    const handleSave = async () => {
        if (!token || !requestId) return;

        setSaving(true);
        try {
            // Prepare selections object (basic implementation matching QuotesReview dashboard logic)
            // We need to construct quotes_selections based on selected IDs
            // Map selected IDs to selection objects
            const quotesSelections = selectedSupplierIds.map(id => {
                const quote = quotes.find(q => String(q.supplier_id) === id);
                if (!quote) return null;

                // Find existing selection to preserve details if any
                let existingSelection = {};
                if (requestDetails?.quotes_selections) {
                    const existing = requestDetails.quotes_selections.find((s: any) =>
                        String(s.supplier_id || s.supplier?._id) === id
                    );
                    if (existing) existingSelection = existing;
                }

                return {
                    ...existingSelection,
                    supplier_id: quote.supplier_id,
                    supplier_name: quote.supplier_name,
                    total_price: quote.total_price,
                    currency: quote.currency,
                    lead_time: quote.lead_time,
                    payment_terms: quote.payment_terms,
                    quote_valid_until: quote.quote_valid_until,
                    source: 'inbox_modal',
                    last_updated: new Date().toISOString()
                };
            }).filter(Boolean);

            const payload = {
                selected_supplier_ids: selectedSupplierIds,
                quotes_selections: quotesSelections,
                phase: 'completed', // Move to completed phase after supplier selection
                status: 'completed'
            };

            const response = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Failed to save selection');
            }

            const result = await response.json();
            if (result.success) {
                if (onSuccess) onSuccess();
                onClose();
            } else {
                throw new Error(result.error || 'Failed to save');
            }
        } catch (err: any) {
            setError(err.message || 'Error saving selection');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="supplier-selection-modal-overlay">
            <div className="supplier-selection-modal">
                <div className="supplier-selection-modal__header">
                    <h3>Select Supplier for {rfqName || 'RFQ'}</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="supplier-selection-modal__content">
                    {loading ? (
                        <div className="loading-state">Loading quotes...</div>
                    ) : error ? (
                        <div className="error-state">{error}</div>
                    ) : quotes.length === 0 ? (
                        <div className="empty-state">No quotes received for this request.</div>
                    ) : (
                        <div className="quotes-list">
                            <div className="quotes-header">
                                <div className="col-checkbox"></div>
                                <div className="col-supplier">Supplier</div>
                                <div className="col-price">Total Price</div>
                                <div className="col-delivery">Lead Time</div>
                                <div className="col-payment">Payment</div>
                            </div>
                            {quotes.map(quote => (
                                <div
                                    key={quote.supplier_id}
                                    className={`quote-row ${selectedSupplierIds.includes(String(quote.supplier_id)) ? 'selected' : ''}`}
                                    onClick={() => handleToggleSupplier(String(quote.supplier_id))}
                                >
                                    <div className="col-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedSupplierIds.includes(String(quote.supplier_id))}
                                            onChange={() => { }} // Handled by row click
                                        />
                                    </div>
                                    <div className="col-supplier">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <span className="supplier-name">{quote.supplier_name}</span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent row click
                                                    setAttachmentSupplier({
                                                        id: quote.supplier_id,
                                                        name: quote.supplier_name
                                                    });
                                                    setShowAttachmentsModal(true);
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    color: '#2563eb',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                {`View Attachments ${typeof attachmentCounts[quote.supplier_id] === 'number' ? `(${attachmentCounts[quote.supplier_id]})` : ''}`}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-price">
                                        {formatPrice(quote)}
                                    </div>
                                    <div className="col-delivery">{quote.lead_time || '-'}</div>
                                    <div className="col-payment">{quote.payment_terms || '-'}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="supplier-selection-modal__footer">
                    <div className="selected-count">
                        {selectedSupplierIds.length} supplier{selectedSupplierIds.length !== 1 ? 's' : ''} selected
                    </div>
                    <div className="actions">
                        <button className="btn-cancel" onClick={onClose}>Cancel</button>
                        <button
                            className="btn-save"
                            onClick={handleSave}
                            disabled={saving || loading || quotes.length === 0}
                        >
                            {saving ? 'Saving...' : 'Confirm Selection'}
                        </button>
                    </div>
                </div>
            </div>
            {attachmentSupplier && requestId && (
                <QuoteAttachmentsModal
                    isOpen={showAttachmentsModal}
                    onClose={() => {
                        setShowAttachmentsModal(false);
                        setAttachmentSupplier(null);
                    }}
                    requestId={requestId}
                    supplierId={attachmentSupplier.id}
                    supplierName={attachmentSupplier.name}
                />
            )}
        </div>
    );
};

export default SupplierSelectionModal;
